/**
 * The "Download to Excel" pricing-and-availability export of a set of lines: the cart
 * (`components/ShoppingCart/ShoppingCartDesktop.tsx` → `downloadExcel`), quote details
 * (`components/QuoteDetails/QuoteDetails.tsx`) and the shopping-list price view
 * (`components/ShoppingListDetails/Helpers/ViewPrice/ViewPriceModal.tsx` → `downloadCartHandler`).
 * It joins each line with its product details, its prices from the caller's pricing response and
 * its Items delivery-times entry, and writes the sheet through
 * `../ExcelExport.tsx` → `exportToExcel` as `pricingAvailability.xlsx`.
 *
 * Rules a change must keep:
 * - Price rule: "the customer is never shown a price the Prices or Quotes API didn't return";
 *   "the APIs may return a $0 value for price in which case we always show "TBD" to the end user
 *   and never "$0""; "All other dollar values show to the user follow the $0 shows "TBD" rule."
 *   Amounts that may be hidden instead: "fuel and energy surcharge, product details price at
 *   block, and order special charges are okay to hide, everything else should be TBD."
 *   In this sheet a zero or missing net price, extended price or weight is written as "TBD". When
 *   any line's net price is "TBD", the subtotal, taxes, fuel surcharge and total net price are
 *   "TBD" too; when any line's weight is, so is the total order weight. A zero or missing
 *   subtotal, tax, fuel surcharge or total is also written as "TBD". The order special charges
 *   row is added only when the amount is non-zero.
 * - Lines are matched to pricing items and delivery-times entries by position, not only by SKU:
 *   the line at index `i` (or, for a shopping-list line, `positionId - 1`) is pricing item
 *   `itemNumber` `i + 1` (`../ExcelExportUtils.tsx` → `getPricePerUomAndExtendedPriceOfItem`). The
 *   caller's line, pricing and delivery-times arrays must therefore come from the same line order.
 * - Stock shows "TBD" when the lead-time day has a buyout lead time or a vendor number, the same
 *   test as `components/Shared/AvailableStock/AvailableStock.tsx`. A line with more than one
 *   lead-time day gets one extra "See above" row per additional day, carrying that day's stock and
 *   date.
 * - A column appears only when at least one row has a value for it (`undefined` means "no value"),
 *   so cut and special-charge columns are left out when no line has them. The order is
 *   `productHeaders`.
 *
 * Item special charges and the line total with them are written only when non-zero, so a line
 * without item special charges has blank cells in those columns.
 *
 * The total net price is `totalPrice` minus `discount.amount`. In the cart export `totalPrice`
 * already includes the discount, so the cart's discount is taken off twice: a known defect (see
 * the comment at the `content.totalNetPrice` row).
 *
 * Related: `./Content.ts` (labels, keyed by the `ExcelProductDetails` field names),
 * `unit-tests/shared/AvailableStockDownloadData.spec.tsx` (stock and "See above" rows).
 */
import {
	displayPrice,
	ProcessingAttributesData,
	Product,
	LanguageToLocale,
	emDash,
	ShoppingListItem,
} from "@ryerson/frontend.common";
import { DefaultUOMDisplay } from "@ryerson/common.uom";
import { ShoppingCartItem, LineItem } from "@ryerson/frontend.e-commerce";
import { IDeliveryTimesResponse } from "@ryerson/common.models";
import { Language, UserProfile } from "@ryerson/frontend.application";
import { LowerCaseLocales } from "@enums/local.enum";
import { formatDateToString } from "@components/Helper/utilities";
import { parseDateSafely } from "@components/Helper/common";
import { getProductCutAttributes } from "@components/Shared/DefineCutModal/DefineCutHelper";
import { processingText } from "@components/Shared/ProcessingDescription";
import {
	displayAttributeValue,
	isMissingProcessAttribute,
} from "@components/Shared/DisplayDefineCut/DefineCutHelper";
import { ProcessingExtended } from "@components/Shared/ProcessingDescription";
import { CurrencyCode } from "@ryerson/common.enums";
import {
	isZero,
	getValues,
	isLineItem,
	getPricePerUomAndExtendedPriceOfItem,
	CutProcessingDetails,
	formatWeight,
	displayCompanyName,
} from "../ExcelExportUtils";
import { ExcelExportContent } from "./Content";
import { exportToExcel } from "../ExcelExport";

/**
 * One row of the product sheet. Field names are also the label keys in `./Content.ts`. Optional
 * fields left `undefined` on every row drop their column.
 */
export type ExcelProductDetails = {
	partNumber: string;
	altType: string;
	alt: string;
	description: string;
	requestedQuantity: string | number;
	requestedUOM: string;
	cutDescription?: string;
	cut?: string;
	cutDimension1?: string;
	cutDimension2?: string;
	cutShape?: string;
	process?: string;
	tolerance1?: string;
	tolerance2?: string;
	totalStock: string | number;
	quantityUOM: string;
	deliveryDate: string;
	weight: string | number;
	netPrice: string | number;
	priceUOM: string;
	extendedPrice: string;
	lineLevelPO: string;
	requestFutureDeliveryDate: string;
	customerReferenceNumber: string;
	lineItemNote: string;
	domesticRequested: boolean | string;
	totalExtendedPrice?: string;
	itemSpecialCharges?: string;
};

/**
 * Pricing for the lines being exported. The cart
 * (`components/ShoppingCart/ShoppingCartDesktop.tsx`) and the shopping-list price view
 * (`components/ShoppingListDetails/Helpers/ViewPrice/ViewPriceModal.tsx`) cast the pricing
 * response they already hold to this shape. Quote details builds it from the
 * quote (`components/QuoteDetails/QuoteDetails.tsx` → `downloadCartHandler`), renaming fields
 * (`subTotal` → `subtotal`, `taxes` → `taxAmount`, `fuelSurcharges` → `fuelSurcharge`,
 * `quoteSpecialCharges` → `orderSpecialCharges`) and numbering `items` by index. `items` is
 * matched to lines by 1-based `itemNumber`.
 */
export interface ExcelPricingDetails {
	totalWeight: string | number;
	totalPrice: string | number;
	subtotal: string | number;
	orderSpecialCharges?: string | number;
	fuelSurcharge?: string | number;
	taxAmount: string | number;
	discount?: { amount: string | number; code: string };
	currency?: CurrencyCode | string;
	items: ExcelItems[];
}

/**
 * One line's prices. `itemNumber` is the line's 1-based position. Same shape as the private
 * `ExcelItems` in `../ExcelExportUtils.tsx`.
 */
export interface ExcelItems {
	itemNumber: string | number;
	extendedPrice: number;
	pricePerUom: string | number;
	weight: string | number;
	totalExtendedPrice?: number;
	itemSpecialCharges?: number;
}

/**
 * Which screen is exporting. It decides how a line finds its delivery-times entry: a shopping-list
 * line by SKU and `itemNumber === positionId`, a cart or quote line by SKU and array index.
 */
export type ExcelExportMode = "cart" | "quote" | "shoppingList";

const productHeaders: (keyof ExcelProductDetails)[] = [
	"partNumber",
	"altType",
	"alt",
	"description",
	"requestedQuantity",
	"requestedUOM",
	"cutDescription",
	"cut",
	"cutDimension1",
	"cutDimension2",
	"cutShape",
	"process",
	"tolerance1",
	"tolerance2",
	"totalStock",
	"quantityUOM",
	"deliveryDate",
	"weight",
	"netPrice",
	"priceUOM",
	"extendedPrice",
	"itemSpecialCharges",
	"totalExtendedPrice",
	"lineLevelPO",
	"requestFutureDeliveryDate",
	"customerReferenceNumber",
	"lineItemNote",
	"domesticRequested",
];

const getExcelSheetHeaders = (items: ExcelProductDetails[]): (keyof ExcelProductDetails)[] => {
	// as the header order matters
	return productHeaders.filter((header) => items.some((item) => item[header] !== undefined));
};

/**
 * Builds and downloads the pricing-and-availability sheet for `products`.
 *
 * @param products - the lines, in the order the pricing and delivery-times responses were built.
 * @param productDetails - product records (cart, list) or quote lines; a line's record is the one
 *   whose `materialMaster` (or, for a quote line, `itemSkuNumber`) equals the line's SKU.
 * @param productsDeliveryTimes - the Items delivery-times response for the same lines.
 * @param purchaseOrderNumber - the order-level PO, written as metadata; each line's own PO is a
 *   column.
 * @param customerPickup - written as "Yes" / "No" in the metadata.
 */
export const exportProductsToExcel = (
	pricing: ExcelPricingDetails,
	products: ShoppingCartItem[] | ShoppingListItem[] | LineItem[] = [],
	productDetails: Product[] | LineItem[] = [],
	productsDeliveryTimes: IDeliveryTimesResponse[],
	purchaseOrderNumber: string,
	profile: UserProfile,
	language: Language,
	customerPickup: boolean,
	mode: ExcelExportMode
) => {
	const content = ExcelExportContent[language];

	const productItems: ExcelProductDetails[] = [];

	products.forEach((item, rowIndex) => {
		const {
			quantity,
			quantityUom,
			pricingUom,
			customerRefNumber,
			notes,
			domesticRequired,
			ryersonSku,
			processing,
		} = getValues(item);

		const itemDetails = (productDetails as (Product | LineItem)[]).find(
			(itemDetail: Product | LineItem) => {
				if (isLineItem(itemDetail)) {
					return (itemDetail as LineItem).itemSkuNumber === ryersonSku;
				} else {
					return (itemDetail as Product).materialMaster === ryersonSku;
				}
			}
		);

		const getItemNumber = () => {
			const { positionId } = item as ShoppingListItem;
			//for view price modal itemNumber is set to positionId but not index
			return positionId ? positionId - 1 : rowIndex;
		};

		const itemDeliveryDate = productsDeliveryTimes.find((product, index) => {
			if (mode === "shoppingList") {
				const { positionId } = item as ShoppingListItem;
				return product.materialSku === ryersonSku && product.itemNumber === positionId;
			} else if (mode === "cart" || mode === "quote") {
				return product.materialSku === ryersonSku && index === getItemNumber();
			}
			return false;
		}) as IDeliveryTimesResponse;

		const { totalWeight, pricePerUom, extendedPrice, itemSpecialCharges, totalExtendedPrice } =
			getPricePerUomAndExtendedPriceOfItem(getItemNumber(), pricing.items);

		let currentPricePerUom = Number(pricePerUom);
		const updatedPricePerUom = Number.isNaN(currentPricePerUom)
			? pricePerUom
			: displayPrice(currentPricePerUom);

		let currentExtendedPrice = Number(extendedPrice);
		let updatedExtendedPrice = Number.isNaN(currentExtendedPrice)
			? extendedPrice
			: displayPrice(currentExtendedPrice);

		const processingData =
			(itemDetails &&
				itemDetails.processingAttribute &&
				itemDetails["processingAttribute"][LowerCaseLocales[language]]) ||
			({} as ProcessingAttributesData);

		const defineCutParameters = processing || {};

		// The cut fields to show are looked up with the English cut definition; their values below
		// come from the processing attributes in the user's language.
		const cutAttributesObjects = getProductCutAttributes(
			Language.en,
			itemDetails as Product,
			defineCutParameters?.shape || ""
		);

		let displayFields: string[] = [];

		cutAttributesObjects.forEach((element) => {
			if (!isMissingProcessAttribute(Object.keys(element).toString(), defineCutParameters)) {
				displayFields.push(Object.keys(element).toString());
			}
		});

		const cutProcessingDetails: CutProcessingDetails = {};

		displayFields.forEach((processField) => {
			cutProcessingDetails[processField as keyof CutProcessingDetails] =
				displayAttributeValue(
					processField,
					processingData,
					processing as ProcessingExtended,
					itemDetails as Product
				);
		});

		const {
			cutShape,
			processing: process,
			outsideDiameter,
			outsideTolerance,
			insideDiameter,
			insideTolerance,
		} = cutProcessingDetails;

		const formattedProcessing =
			processing &&
			processingText(
				processing,
				itemDetails?.processingAttribute || {},
				true,
				language,
				itemDetails?.tolerances || undefined
			);

		// An `altPartNumber` of the form "CPN:<number>" is the customer's own part number; the Alt
		// column then shows that number with Alt Type "CPN", otherwise the Ryerson SKU with "ALT".
		const hasCPN = itemDetails?.altPartNumber?.includes("CPN:");

		const { leadTimeDays } = itemDeliveryDate || {};

		const requestedDeliveryDate = (item as ShoppingCartItem)?.requestedDeliveryDate;

		const itemDescription =
			itemDetails && "description" in itemDetails
				? (itemDetails?.description as string)
				: itemDetails?.itemDescriptionLocalization?.[LanguageToLocale[language]]
						?.shortDescription;
		const itemAlt =
			itemDetails && !("materialMaster" in itemDetails)
				? itemDetails?.itemSkuNumber
				: itemDetails?.materialMaster;

		productItems.push({
			partNumber: itemDetails?.fastPath || "",
			altType: hasCPN ? "CPN" : "ALT",
			alt: (hasCPN ? itemDetails?.altPartNumber?.split(":")[1] : itemAlt) || "",
			description:
				(itemDescription &&
					`${formattedProcessing ? content["cutFrom"] : ""}${itemDescription}`) ||
				"",
			requestedQuantity: quantity || "",
			requestedUOM:
				(quantityUom && DefaultUOMDisplay[quantityUom as keyof typeof DefaultUOMDisplay]) ||
				"",
			// The cut text from `processingText`, without its prefix. The strip matches only the
			// English "Cut to: ", so in French and Spanish the translated prefix ("Couper en:",
			// "Cortar en:") stays in the cell. The rest of the text is under the language rule in
			// the header of `components/Shared/ProcessingDescription/ProcessingDescription.tsx` and
			// carries the English labels and names that header lists as known defects.
			cutDescription: formattedProcessing
				? formattedProcessing?.replace("Cut to: ", "").trim()
				: undefined,
			cut: formattedProcessing ? "Yes" : undefined,
			cutDimension1: outsideDiameter || insideDiameter || undefined,
			cutDimension2: insideDiameter || undefined,
			cutShape: cutShape || undefined,
			process: process || undefined,
			tolerance1: outsideTolerance || insideTolerance || undefined,
			tolerance2: insideTolerance || undefined,
			totalStock: !!(
				leadTimeDays?.[0]?.buyoutLeadTime || leadTimeDays?.[0]?.availabilityPlant?.vendorNo
			)
				? content["tbd"]
				: (leadTimeDays?.[0]?.availabilityPlant?.quantityAvailable ?? content["tbd"]),
			quantityUOM:
				(quantityUom && DefaultUOMDisplay[quantityUom as keyof typeof DefaultUOMDisplay]) ||
				"",
			deliveryDate: leadTimeDays?.[0].deliveryDate || content["tbd"],
			weight:
				totalWeight && totalWeight !== emDash ? formatWeight(totalWeight) : content["tbd"],
			netPrice:
				updatedPricePerUom && !isZero(updatedPricePerUom) && updatedPricePerUom !== emDash
					? updatedPricePerUom
					: content["tbd"],
			priceUOM:
				(pricingUom && DefaultUOMDisplay[pricingUom as keyof typeof DefaultUOMDisplay]) ||
				"",
			extendedPrice:
				updatedExtendedPrice &&
				updatedExtendedPrice !== emDash &&
				!isZero(updatedExtendedPrice)
					? updatedExtendedPrice
					: content["tbd"],
			itemSpecialCharges: itemSpecialCharges ? displayPrice(itemSpecialCharges) : undefined,
			totalExtendedPrice: totalExtendedPrice ? displayPrice(totalExtendedPrice) : undefined,
			lineLevelPO: item.purchaseOrderNumber || "",
			requestFutureDeliveryDate:
				(requestedDeliveryDate &&
					formatDateToString(parseDateSafely(requestedDeliveryDate) || new Date())) ||
				"",
			customerReferenceNumber: customerRefNumber || "",
			lineItemNote: notes || "",
			domesticRequested: !!domesticRequired,
		});

		if (leadTimeDays?.length > 1) {
			leadTimeDays.slice(1).forEach((leadTime) =>
				productItems.push({
					partNumber: content["seeAbove"],
					altType: content["seeAbove"],
					alt: content["seeAbove"],
					description: content["seeAbove"],
					requestedQuantity: content["seeAbove"],
					requestedUOM: content["seeAbove"],
					totalStock: !!(
						leadTime?.buyoutLeadTime || leadTime?.availabilityPlant?.vendorNo
					)
						? content["tbd"]
						: (leadTime?.availabilityPlant?.quantityAvailable ?? ""),
					quantityUOM:
						(quantityUom &&
							DefaultUOMDisplay[quantityUom as keyof typeof DefaultUOMDisplay]) ||
						"",
					deliveryDate: leadTime.deliveryDate || content["tbd"],
					weight: content["seeAbove"],
					netPrice: content["seeAbove"],
					priceUOM: content["seeAbove"],
					extendedPrice: content["seeAbove"],
					lineLevelPO: content["seeAbove"],
					requestFutureDeliveryDate: content["seeAbove"],
					customerReferenceNumber: content["seeAbove"],
					lineItemNote: content["seeAbove"],
					domesticRequested: content["seeAbove"],
				})
			);
		}
	});

	//If in one or more items,  “Weight (LB)” is zero or null, “Total Order Weight (LB)“ must have a TDB value
	const hasInvalidWeight = productItems.some(
		(item) => item.weight === content["tbd"] || isZero(item.weight)
	);

	//check if in one or more items, “Net Price” is zero or null
	const hasInvalidNetPrice = productItems.some((item) => item.netPrice === content["tbd"]);

	// create metadata object
	const metadata = [
		{ key: content.customerNumber, value: profile?.customer?.customerNumber || "" },
		{
			key: content.totalOrderWeight,
			value: !hasInvalidWeight ? formatWeight(pricing.totalWeight) : content["tbd"],
		},
		{
			key: content.subTotal,
			value:
				!hasInvalidNetPrice && pricing.subtotal
					? displayPrice(Number(pricing.subtotal))
					: content["tbd"],
		},
		{
			key: content.taxes,
			value:
				!hasInvalidNetPrice && pricing.taxAmount
					? displayPrice(Number(pricing.taxAmount))
					: content["tbd"],
		},
		{
			key: content.fuelSurcharge,
			value:
				!hasInvalidNetPrice && pricing.fuelSurcharge
					? displayPrice(Number(pricing.fuelSurcharge))
					: content["tbd"],
		},
		{
			key: content.discountApplied,
			value: pricing.discount?.amount
				? `-${displayPrice(Number(pricing.discount?.amount))}`
				: "",
		},
		{ key: content.customerPickup, value: customerPickup ? "Yes" : "No" },
		{ key: content.currency, value: pricing.currency || "" },
		// `totalPrice` minus `discount.amount`. In the cart export `totalPrice` is the Prices API's
		// total: ERP's total, which already includes the discount, or, when ERP's total is 0 or
		// absent, subtotal + tax + fuel surcharge, where ERP's subtotal is already after the
		// discount. Subtracting the discount here takes it off a second time, so the sheet's total
		// is lower than the total the Prices API returned: a known defect. Quote details passes a
		// discount of 0, and the shopping-list price request sends no coupon code.
		{
			key: content.totalNetPrice,
			value:
				!hasInvalidNetPrice && pricing.totalPrice
					? displayPrice(
							Number(pricing.totalPrice) - Number(pricing.discount?.amount || 0)
						)
					: content["tbd"],
		},
		{ key: content.timeStamp, value: new Date().toLocaleString() },
		{ key: content.purchaseOrderNumber, value: purchaseOrderNumber || "" },
		{
			key: content.note,
			value: content["noteText"].replace(/%company%/g, displayCompanyName),
			link: `${window?.location.origin}/terms-and-conditions/`,
			tooltipContent: content["noteLinkTooltip"].replace(/%company%/g, displayCompanyName),
			skipWidthCalculation: true,
		},
	];

	// add special charges when available
	// Index 3 puts the row directly after Subtotal.
	if (pricing.orderSpecialCharges) {
		metadata.splice(3, 0, {
			key: content.orderSpecialCharges,
			value: displayPrice(Number(pricing.orderSpecialCharges)),
		});
	}

	// create row headers
	const activeHeaders = getExcelSheetHeaders(productItems);
	const headers = activeHeaders.map((header) => content[header as keyof ExcelProductDetails]);

	// filter data
	const filteredData = productItems.map((row) => {
		const filteredRow: Record<string, ExcelProductDetails[keyof ExcelProductDetails]> = {};
		activeHeaders.forEach((header) => {
			filteredRow[header] = row[header];
		});
		return filteredRow;
	});

	exportToExcel(metadata, headers, filteredData, "pricingAvailability");
};
